import React, { useState, useEffect, useRef } from 'react';
import './Contact.css';

function FadeSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-in ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const participationTypes = [
  { value: 'plant-love', label: '🌲 Plant a Love Tree', desc: 'Valentine\'s Day campaign' },
  { value: 'volunteer', label: '🤝 Become a Volunteer', desc: 'Join field activities' },
  { value: 'tree-drive', label: '🌳 Tree Planting Drive', desc: 'Organize/join a drive' },
  { value: 'clean-drive', label: '🧹 Clean Drive', desc: 'Community cleanup' },
  { value: 'agri-partner', label: '🌾 Agri Partnership', desc: 'Farmer/business partner' },
  { value: 'donation', label: '💚 Support / Donate', desc: 'Fund our initiatives' },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    participation: '',
    message: '',
    agree: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  return (
    <div className="contact-page">
      {/* HERO */}
      <section className="page-hero contact-hero">
        <div className="contact-hero__bg" />
        <div className="page-hero__overlay" />
        <div className="container page-hero__content">
          <span className="tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
            Join the Movement
          </span>
          <h1 className="page-hero__title">
            Register Your<br />
            <span style={{ color: 'var(--gold-light)' }}>Participation & Share Your Love</span>
          </h1>
          <p className="page-hero__sub">
            Whether you want to plant a tree, volunteer, partner with us, or just say hello —
            we'd love to hear from you. Together, let's make the world greener.
          </p>
        </div>
      </section>

      <section className="contact-section section-padding">
        <div className="container">
          <div className="contact-grid">
            {/* FORM */}
            <FadeSection>
              <div className="contact-form-wrapper">
                {submitted ? (
                  <div className="contact-success">
                    <div className="contact-success__icon">🌱</div>
                    <h2>You're Registered!</h2>
                    <p>
                      Thank you for joining Y2K Global Nexus and the CallTree movement.
                      Our team will reach out to you shortly to confirm your participation.
                    </p>
                    <div className="contact-success__quote">
                      <em>
                        "Memories are ever Green — Greener trees hold Green Memories.
                        You now live in her / his memories forever."
                      </em>
                    </div>
                    <button className="btn-primary" onClick={() => setSubmitted(false)} style={{ marginTop: '24px' }}>
                      Register Another
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="contact-form-header">
                      <h2>Register Your Participation</h2>
                      <p>Fill in the form below and we'll get back to you within 24 hours.</p>
                    </div>

                    <form className="contact-form" onSubmit={handleSubmit}>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="name">Full Name *</label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="phone">Phone Number *</label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="+91 xxxxx xxxxx"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="email">Email Address</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="city">City / Location *</label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={form.city}
                            onChange={handleChange}
                            placeholder="Your city"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>How would you like to participate? *</label>
                        <div className="participation-grid">
                          {participationTypes.map(({ value, label, desc }) => (
                            <label key={value} className={`participation-option ${form.participation === value ? 'participation-option--selected' : ''}`}>
                              <input
                                type="radio"
                                name="participation"
                                value={value}
                                checked={form.participation === value}
                                onChange={handleChange}
                              />
                              <div className="participation-option__content">
                                <span className="participation-option__label">{label}</span>
                                <span className="participation-option__desc">{desc}</span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">Message (Optional)</label>
                        <textarea
                          id="message"
                          name="message"
                          value={form.message}
                          onChange={handleChange}
                          placeholder="Tell us more about how you'd like to participate, or ask any questions..."
                          rows={4}
                        />
                      </div>

                      <div className="form-group form-group--checkbox">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="agree"
                            checked={form.agree}
                            onChange={handleChange}
                            required
                          />
                          <span>
                            I agree to be contacted by Y2K Global Nexus regarding my participation.
                            I understand this is for environmental activities coordinated with the Forest Department.
                          </span>
                        </label>
                      </div>

                      <button
                        type="submit"
                        className={`btn-primary contact-submit ${loading ? 'contact-submit--loading' : ''}`}
                        disabled={loading || !form.agree}
                      >
                        {loading ? '🌱 Registering...' : '🌱 Register My Participation'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </FadeSection>

            {/* SIDEBAR */}
            <FadeSection delay={200}>
              <div className="contact-sidebar">
                <div className="contact-info-card">
                  <h3>Get in Touch</h3>
                  <div className="contact-info-items">
                    <div className="contact-info-item">
                      <span className="contact-info-item__icon">🌐</span>
                      <div>
                        <strong>Website</strong>
                        <span>calltree.in</span>
                      </div>
                    </div>
                    <div className="contact-info-item">
                      <span className="contact-info-item__icon">📧</span>
                      <div>
                        <strong>Email</strong>
                        <span>info@calltree.in</span>
                      </div>
                    </div>
                    <div className="contact-info-item">
                      <span className="contact-info-item__icon">🏛️</span>
                      <div>
                        <strong>Forest Department</strong>
                        <span>Official Partner</span>
                      </div>
                    </div>
                    <div className="contact-info-item">
                      <span className="contact-info-item__icon">📍</span>
                      <div>
                        <strong>Presence</strong>
                        <span>50+ Cities, Pan India</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="contact-poem-card">
                  <div className="contact-poem-card__icon">🌹</div>
                  <h3>Plant a Love</h3>
                  <p>
                    "Come on Lets get cleansed<br />
                    On this valentine's day<br />
                    Register your participation<br />
                    and share your Love"
                  </p>
                  <div className="contact-poem-card__credit">— Y2K Global Nexus × CallTree</div>
                </div>

                <div className="contact-impact-card">
                  <h3>Your Impact</h3>
                  <div className="contact-impact-items">
                    <div className="contact-impact-item">
                      <span>🌳</span>
                      <p>1 registration = 1 tree planted with Forest Dept.</p>
                    </div>
                    <div className="contact-impact-item">
                      <span>💨</span>
                      <p>1 tree = 48 lbs of CO₂ absorbed annually</p>
                    </div>
                    <div className="contact-impact-item">
                      <span>🌍</span>
                      <p>Your memory goes global with Y2K</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeSection>
          </div>
        </div>
      </section>
    </div>
  );
}
